/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlfrescoApiService, TagService } from '@alfresco/adf-content-services';
import { NotificationService } from '@alfresco/adf-core';
import { ContentApiService, ThaiDatePipe } from '@alfresco/aca-shared';
import { Node, NodesApi, VersionsApi } from '@alfresco/js-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AssistantService } from '../assistant/assistant.service';
import { OcrService } from '../ocr/ocr.service';
import { OcrPipelineService } from '../ocr/ocr-pipeline.service';

/** One entry of the version history. */
interface VersionRow {
  id: string;
  name: string;
  comment: string;
  modifiedAt: string;
  author: string;
  initial: string;
  sizeInBytes: number;
  mimeType: string;
  /** Whether this is the version the document currently serves. */
  current: boolean;
  major: boolean;
}

type VersionFilter = 'all' | 'major';

/** A readable name for the file kinds this repository actually holds. */
const TYPE_NAMES: Record<string, string> = {
  'image/png': 'PNG Image',
  'image/jpeg': 'JPEG Image',
  'image/gif': 'GIF Image',
  'image/webp': 'WebP Image',
  'application/pdf': 'PDF Document',
  'application/json': 'JSON',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'application/msword': 'Word Document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
  'application/vnd.ms-excel': 'Excel Workbook',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Workbook',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint'
};

/**
 * Version history for one document (UI-20).
 *
 * The reference draws the history as a timeline with the details of whichever
 * version is picked beside it, which is neither the shape nor the content of
 * ADF's version manager — so the history is read from the repository here and
 * laid out as the reference asks.
 */
@Component({
  selector: 'app-document-history',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatMenuModule, MatProgressBarModule, MatTooltipModule, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-history.component.html',
  styleUrl: './document-history.component.scss'
})
export class DocumentHistoryComponent implements OnChanges {
  @Input({ required: true }) node!: Node;

  /** Asked for when the reader follows the access-rights line of the details. */
  @Output() showPermissions = new EventEmitter<void>();

  private readonly api = inject(AlfrescoApiService);
  private readonly content = inject(ContentApiService);
  private readonly tagService = inject(TagService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly ocr = inject(OcrService);
  private readonly pipeline = inject(OcrPipelineService);
  readonly assistant = inject(AssistantService);

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly versions = signal<VersionRow[]>([]);
  readonly selectedId = signal<string | null>(null);
  readonly filter = signal<VersionFilter>('all');
  readonly tags = signal<{ id: string; tag: string }[]>([]);

  /** The pending upload, once a file has been chosen but not yet sent. */
  readonly pendingFile = signal<File | null>(null);
  uploadComment = '';
  uploadMajor = true;

  /** The two versions being compared, when the reader asked for a comparison. */
  readonly comparing = signal(false);
  compareA = '';
  compareB = '';

  /** The AI's read of the two picked versions — asked for, never automatic. */
  readonly aiBusy = signal(false);
  readonly aiChanges = signal<string | null>(null);
  readonly aiBy = signal<string | null>(null);

  readonly filters: { id: VersionFilter; label: string }[] = [
    { id: 'all', label: 'DMS.HISTORY.FILTER_ALL' },
    { id: 'major', label: 'DMS.HISTORY.FILTER_MAJOR' }
  ];

  readonly visible = computed(() => {
    const only = this.filter() === 'major';
    return this.versions().filter((version) => !only || version.major);
  });

  readonly current = computed(() => this.versions().find((version) => version.current) ?? null);
  readonly selected = computed(() => this.versions().find((version) => version.id === this.selectedId()) ?? this.current());

  readonly rowA = computed(() => this.versions().find((version) => version.id === this.compareA) ?? null);
  readonly rowB = computed(() => this.versions().find((version) => version.id === this.compareB) ?? null);

  /** Where the document sits, as the trail of folders the reference shows. */
  readonly location = computed(() => {
    const elements = (this.node?.path?.elements ?? []) as { id: string; name: string }[];
    return elements.filter((element) => element.name !== 'Company Home' || elements.length === 1).slice(-3);
  });

  ngOnChanges(): void {
    if (this.node?.id) {
      this.load();
      this.loadTags();
    }
  }

  load(): void {
    this.loading.set(true);
    const versions = new VersionsApi(this.api.getInstance() as any);
    (versions.listVersionHistory(this.node.id, { maxItems: 200 } as any) as Promise<any>)
      .then((paging) => {
        const entries = (paging?.list?.entries ?? []).map((entry: any) => entry.entry);
        const rows: VersionRow[] = entries.map((entry: any, index: number) => {
          const author = entry.modifiedByUser?.displayName || entry.modifiedByUser?.id || '—';
          return {
            id: entry.id,
            name: entry.name,
            comment: entry.versionComment || '',
            modifiedAt: entry.modifiedAt,
            author,
            initial: (author.trim()[0] ?? '?').toUpperCase(),
            sizeInBytes: entry.content?.sizeInBytes ?? 0,
            mimeType: entry.content?.mimeType ?? '',
            current: index === 0,
            major: /\.0$/.test(entry.id ?? '')
          };
        });
        this.versions.set(rows);
        this.selectedId.set(rows[0]?.id ?? null);
        this.compareA = rows[0]?.id ?? '';
        this.compareB = rows[1]?.id ?? '';
        this.loading.set(false);
      })
      .catch(() => {
        this.versions.set([]);
        this.loading.set(false);
        this.notification.showError(this.translate.instant('DMS.HISTORY.LOAD_FAIL'));
      });
  }

  private loadTags(): void {
    this.tagService.getTagsByNodeId(this.node.id).subscribe({
      next: (paging: any) => this.tags.set((paging?.list?.entries ?? []).map((entry: any) => ({ id: entry.entry.id, tag: entry.entry.tag }))),
      error: () => this.tags.set([])
    });
  }

  // ── labels ─────────────────────────────────────────────────────────────────

  /** "1.24 MB (1,296,872 ไบต์)", as the reference writes it. */
  sizeText(bytes: number): string {
    return `${this.shortSize(bytes)} (${bytes.toLocaleString('th-TH')} ${this.translate.instant('DMS.HISTORY.BYTES')})`;
  }

  shortSize(bytes: number): string {
    if (!bytes) {
      return '0 B';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  typeName(mimeType: string): string {
    return TYPE_NAMES[mimeType] ?? (mimeType ? mimeType.split('/').pop().toUpperCase() : '—');
  }

  filterLabel(): string {
    const entry = this.filters.find((option) => option.id === this.filter());
    return entry ? this.translate.instant(entry.label) : '';
  }

  // ── picking things ─────────────────────────────────────────────────────────

  select(version: VersionRow): void {
    this.selectedId.set(version.id);
  }

  setFilter(filter: VersionFilter): void {
    this.filter.set(filter);
  }

  openCompare(): void {
    const rows = this.versions();
    if (rows.length < 2) {
      this.notification.showInfo(this.translate.instant('DMS.HISTORY.COMPARE_NEEDS_TWO'));
      return;
    }
    this.compareA = this.selected()?.id ?? rows[0].id;
    this.compareB = rows.find((row) => row.id !== this.compareA)?.id ?? rows[1].id;
    this.comparing.set(true);
    this.pickCompare();
  }

  compareWith(versionId: string): void {
    if (!versionId) {
      return;
    }
    this.compareA = this.selected()?.id ?? this.versions()[0]?.id ?? '';
    this.compareB = versionId;
    this.comparing.set(true);
    this.pickCompare();
  }

  closeCompare(): void {
    this.comparing.set(false);
    this.aiChanges.set(null);
    this.aiBy.set(null);
  }

  /** A stale answer belongs to the pair it was read for, not the new pick. */
  pickCompare(): void {
    this.aiChanges.set(null);
    this.aiBy.set(null);
  }

  /**
   * Asks the AI what changed between the two picked versions. The index holds
   * only the current version's words, so an older version's bytes are fetched
   * and read here — by pdf.js for a text layer, by the OCR engine for a scan.
   */
  aiCompare(): void {
    const rows = this.versions();
    const a = this.rowA();
    const b = this.rowB();
    if (!a || !b || a.id === b.id || this.aiBusy()) {
      return;
    }

    // the list runs newest first, so the one further down is the older text
    const [older, newer] = rows.indexOf(a) > rows.indexOf(b) ? [a, b] : [b, a];
    this.aiBusy.set(true);
    this.aiChanges.set(null);
    this.aiBy.set(null);

    Promise.all([this.versionText(older), this.versionText(newer)])
      .then(([textA, textB]) => {
        if (!textA || !textB) {
          this.aiBusy.set(false);
          this.notification.showInfo(this.translate.instant('DMS.ASSISTANT.COMPARE_NO_TEXT'));
          return;
        }
        this.assistant.compareVersions(this.node.id, { label: `v${older.id}`, text: textA }, { label: `v${newer.id}`, text: textB }).subscribe({
          next: (result) => {
            this.aiBusy.set(false);
            if (result?.changes) {
              this.aiChanges.set(result.changes);
              this.aiBy.set(this.translate.instant('DMS.ASSISTANT.SUMMARY_BY', { provider: result.provider ?? '', model: result.model ?? '' }));
            } else {
              this.notification.showWarning(
                this.translate.instant(result?.reason === 'confidentiality' ? 'DMS.ASSISTANT.SUMMARY_WITHHELD' : 'DMS.ASSISTANT.COMPARE_NO_TEXT')
              );
            }
          },
          error: (error) => {
            this.aiBusy.set(false);
            this.notification.showError(this.translate.instant(error?.status === 403 ? 'DMS.ASSISTANT.SUMMARY_OFF' : 'DMS.ASSISTANT.COMPARE_FAIL'));
          }
        });
      })
      .catch(() => {
        this.aiBusy.set(false);
        this.notification.showError(this.translate.instant('DMS.ASSISTANT.COMPARE_READ_FAIL'));
      });
  }

  /**
   * The words of one version. The current one's are already in the index from
   * its upload; an older one's come out of its file — text layer where it has
   * one, the OCR engine where it is a scan.
   */
  private async versionText(version: VersionRow): Promise<string> {
    if (version.current) {
      const indexed = await new Promise<{ pages?: { text?: string }[] } | null>((resolve) =>
        this.ocr.indexedText(this.node.id).subscribe({ next: resolve, error: () => resolve(null) })
      );
      const text = (indexed?.pages ?? [])
        .map((page) => page.text ?? '')
        .join('\n\n')
        .trim();
      if (text) {
        return text;
      }
    }

    const url = version.current ? this.content.getContentUrl(this.node.id, true) : this.content.getVersionContentUrl(this.node.id, version.id, true);
    const blob = await (await fetch(url)).blob();
    const name = version.name || this.node.name || 'version';
    return this.pipeline.textOf(new File([blob], name), name);
  }

  /** How the two compared versions differ on one field. */
  differs(field: 'sizeInBytes' | 'author' | 'comment' | 'mimeType'): boolean {
    const a = this.rowA();
    const b = this.rowB();
    return !!a && !!b && a[field] !== b[field];
  }

  // ── acting on a version ────────────────────────────────────────────────────

  download(version: VersionRow): void {
    const url = version.current ? this.content.getContentUrl(this.node.id, true) : this.content.getVersionContentUrl(this.node.id, version.id, true);
    window.open(url, '_blank');
  }

  restore(version: VersionRow): void {
    if (version.current || this.busy()) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.HISTORY.RESTORE_CONFIRM', { p0: version.id }))) {
      return;
    }
    this.busy.set(true);
    const versions = new VersionsApi(this.api.getInstance() as any);
    (
      versions.revertVersion(this.node.id, version.id, {
        majorVersion: true,
        comment: this.translate.instant('DMS.HISTORY.RESTORE_COMMENT', { p0: version.id })
      }) as Promise<any>
    )
      .then(() => {
        this.busy.set(false);
        this.notification.showInfo(this.translate.instant('DMS.HISTORY.RESTORED'));
        this.load();
      })
      .catch(() => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.HISTORY.RESTORE_FAIL'));
      });
  }

  remove(version: VersionRow): void {
    if (version.current || this.busy()) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.HISTORY.DELETE_CONFIRM', { p0: version.id }))) {
      return;
    }
    this.busy.set(true);
    const versions = new VersionsApi(this.api.getInstance() as any);
    (versions.deleteVersion(this.node.id, version.id) as Promise<any>)
      .then(() => {
        this.busy.set(false);
        this.notification.showInfo(this.translate.instant('DMS.HISTORY.DELETED'));
        this.load();
      })
      .catch(() => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.HISTORY.DELETE_FAIL'));
      });
  }

  // ── adding a version ───────────────────────────────────────────────────────

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (file) {
      this.pendingFile.set(file);
      this.uploadComment = '';
      this.uploadMajor = true;
    }
  }

  cancelUpload(): void {
    this.pendingFile.set(null);
    this.uploadComment = '';
  }

  upload(): void {
    const file = this.pendingFile();
    if (!file || this.busy()) {
      return;
    }
    this.busy.set(true);
    const nodes = new NodesApi(this.api.getInstance() as any);
    (
      nodes.updateNodeContent(
        this.node.id,
        file as any,
        {
          majorVersion: this.uploadMajor,
          comment: this.uploadComment.trim() || undefined
        } as any
      ) as Promise<any>
    )
      .then(() => {
        this.busy.set(false);
        this.cancelUpload();
        this.notification.showInfo(this.translate.instant('DMS.HISTORY.UPLOADED'));
        this.load();
      })
      .catch(() => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.HISTORY.UPLOAD_FAIL'));
      });
  }

  // ── tags ───────────────────────────────────────────────────────────────────

  readonly addingTag = signal(false);

  @ViewChild('tagInput') private readonly tagInput?: ElementRef<HTMLInputElement>;
  newTag = '';

  startTag(): void {
    this.addingTag.set(true);
    this.newTag = '';
    // the field is only put on the page by the line above, so the caret is sent
    // after this turn rather than by an autofocus attribute, which would also
    // steal focus from a reader arriving at the panel
    setTimeout(() => this.tagInput?.nativeElement.focus());
  }

  addTag(): void {
    const name = this.newTag.trim();
    if (!name) {
      this.addingTag.set(false);
      return;
    }
    this.tagService.addTag(this.node.id, name).subscribe({
      next: () => {
        this.newTag = '';
        this.addingTag.set(false);
        this.loadTags();
      },
      error: () => {
        this.addingTag.set(false);
        this.notification.showError(this.translate.instant('DMS.HISTORY.TAG_FAIL'));
      }
    });
  }

  trackById(_index: number, version: VersionRow): string {
    return version.id;
  }
}
